import React, { useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Switch } from '../../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Save, X, Loader2, Languages, PlusCircle, MinusCircle, Image as ImageIcon } from 'lucide-react';
import ImageLibrary from '../ImageLibrary';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BADGE_OPTIONS = [
  { key: 'badge_artesano', label: { es: 'Artesano', en: 'Artisan', cat: 'Artesà', fr: 'Artisanal' }, icon: '🔨' },
  { key: 'badge_local', label: { es: 'Local', en: 'Local', cat: 'Local', fr: 'Local' }, icon: '📍' },
  { key: 'badge_ecologico', label: { es: 'Ecológico', en: 'Organic', cat: 'Ecològic', fr: 'Bio' }, icon: '🌿' },
  { key: 'badge_edicion_limitada', label: { es: 'Edición Limitada', en: 'Limited Edition', cat: 'Edició Limitada', fr: 'Édition Limitée' }, icon: '⏳' },
  { key: 'badge_premium', label: { es: 'Premium', en: 'Premium', cat: 'Premium', fr: 'Premium' }, icon: '💎' },
  { key: 'badge_recomendado', label: { es: 'Recomendado', en: 'Recommended', cat: 'Recomanat', fr: 'Recommandé' }, icon: '👍' },
  { key: 'badge_natural', label: { es: 'Natural', en: 'Natural', cat: 'Natural', fr: 'Naturel' }, icon: '🍃' },
  { key: 'badge_sin_alcohol', label: { es: 'Sin Alcohol', en: 'Non-Alcoholic', cat: 'Sense Alcohol', fr: 'Sans Alcool' }, icon: '🚫' },
  { key: 'badge_vegano', label: { es: 'Vegano', en: 'Vegan', cat: 'Vegà', fr: 'Végan' }, icon: '🌱' },
  { key: 'badge_sin_gluten', label: { es: 'Sin Gluten', en: 'Gluten-Free', cat: 'Sense Gluten', fr: 'Sans Gluten' }, icon: '🌾' },
  { key: 'badge_bestseller', label: { es: 'Bestseller', en: 'Bestseller', cat: 'Més Venut', fr: 'Best-seller' }, icon: '⭐' },
  { key: 'badge_nuevo', label: { es: 'Nuevo', en: 'New', cat: 'Nou', fr: 'Nouveau' }, icon: '✨' },
];

const DEFAULT_FORM_DATA = {
  name: { es: '', en: '', cat: '', fr: '' },
  description: { es: '', en: '', cat: '', fr: '' },
  price: 0,
  category_id: '',
  is_active: true,
  image_url: '',
  supplements: [],
  origin_seal: { es: '', en: '', cat: '', fr: '' },
  seccion: 'cocina',
  badge_artesano: false, badge_local: false, badge_ecologico: false,
  badge_edicion_limitada: false, badge_premium: false, badge_recomendado: false,
  badge_natural: false, badge_sin_alcohol: false, badge_vegano: false,
  badge_sin_gluten: false, badge_bestseller: false, badge_nuevo: false,
  badge_type: 'none',
  suggestions_mode: 'none',
  recommended_products: [],
  // Experience / Pack
  product_type: 'simple',
  experience_items: [],
  experience_target_people: '1',
  experience_copy: { es: '', en: '', cat: '', fr: '' },
  // Shop
  available_in_shop: false,
  shop_message: { es: '', en: '', cat: '', fr: '' },
  // Upsell al añadir (producto o experiencia)
  upsell_product_id: '',
  upsell_title: { es: '', en: '', cat: '', fr: '' },
  upsell_description: { es: '', en: '', cat: '', fr: '' },
  upsell_cta_accept: { es: '', en: '', cat: '', fr: '' },
  upsell_cta_decline: { es: '', en: '', cat: '', fr: '' },
  // Downsell (si rechaza el upsell)
  downsell_product_id: '',
  downsell_title: { es: '', en: '', cat: '', fr: '' },
  downsell_description: { es: '', en: '', cat: '', fr: '' },
};

export const initFormData = (product, categories) => {
  if (!product) {
    return { ...DEFAULT_FORM_DATA, category_id: categories[0]?.id || '' };
  }
  let originSeal = product.origin_seal || { es: '', en: '', cat: '', fr: '' };
  if (typeof originSeal === 'string') {
    originSeal = { es: originSeal, en: originSeal, cat: originSeal, fr: originSeal };
  }
  return {
    name: product.name || { es: '', en: '', cat: '', fr: '' },
    description: product.description || { es: '', en: '', cat: '', fr: '' },
    price: product.price || 0,
    category_id: product.category_id || '',
    is_active: product.is_active !== false,
    image_url: product.image_url || product.images?.[0] || '',
    supplements: product.supplements || [],
    origin_seal: originSeal,
    seccion: product.seccion || 'cocina',
    badge_artesano: product.badge_artesano || false,
    badge_local: product.badge_local || false,
    badge_ecologico: product.badge_ecologico || false,
    badge_edicion_limitada: product.badge_edicion_limitada || false,
    badge_premium: product.badge_premium || false,
    badge_recomendado: product.badge_recomendado || false,
    badge_natural: product.badge_natural || false,
    badge_sin_alcohol: product.badge_sin_alcohol || false,
    badge_vegano: product.badge_vegano || false,
    badge_sin_gluten: product.badge_sin_gluten || false,
    badge_bestseller: product.badge_bestseller || false,
    badge_nuevo: product.badge_nuevo || false,
    badge_type: product.badge_type || 'none',
    suggestions_mode: product.suggestions_mode || 'none',
    recommended_products: product.recommended_products || [],
    // Experience
    product_type: product.product_type || 'simple',
    experience_items: product.experience_items || [],
    experience_target_people: product.experience_target_people || '1',
    experience_copy: product.experience_copy || { es: '', en: '', cat: '', fr: '' },
    // Shop
    available_in_shop: product.available_in_shop || false,
    shop_message: product.shop_message || { es: '', en: '', cat: '', fr: '' },
    // Upsell al añadir
    upsell_product_id: product.upsell_product_id || '',
    upsell_title: product.upsell_title || { es: '', en: '', cat: '', fr: '' },
    upsell_description: product.upsell_description || { es: '', en: '', cat: '', fr: '' },
    upsell_cta_accept: product.upsell_cta_accept || { es: '', en: '', cat: '', fr: '' },
    upsell_cta_decline: product.upsell_cta_decline || { es: '', en: '', cat: '', fr: '' },
    // Downsell
    downsell_product_id: product.downsell_product_id || '',
    downsell_title: product.downsell_title || { es: '', en: '', cat: '', fr: '' },
    downsell_description: product.downsell_description || { es: '', en: '', cat: '', fr: '' },
  };
};

const ProductFormDialog = ({ open, onOpenChange, formData, setFormData, onSave, saving, editingProduct, categories, allProducts = [] }) => {
  const [translating, setTranslating] = useState(false);
  const [showImageLibrary, setShowImageLibrary] = useState(false);

  const handleAutoTranslate = async () => {
    if (!formData.name.es && !formData.description.es) return;
    setTranslating(true);
    try {
      if (formData.name.es) {
        const nameRes = await fetch(`${API_URL}/api/translate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: formData.name.es, source_lang: 'es', target_langs: ['cat', 'en', 'fr'] })
        });
        const nameData = await nameRes.json();
        if (nameData.translations) {
          setFormData(prev => ({ ...prev, name: { ...prev.name, ...nameData.translations } }));
        }
      }
      if (formData.description.es) {
        const descRes = await fetch(`${API_URL}/api/translate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: formData.description.es, source_lang: 'es', target_langs: ['cat', 'en', 'fr'] })
        });
        const descData = await descRes.json();
        if (descData.translations) {
          setFormData(prev => ({ ...prev, description: { ...prev.description, ...descData.translations } }));
        }
      }
    } catch (error) {
      console.error('Translation error:', error);
    } finally {
      setTranslating(false);
    }
  };

  const handleAddSupplement = () => {
    setFormData(prev => ({
      ...prev,
      supplements: [...prev.supplements, { name: { es: '', en: '', cat: '', fr: '' }, price_modifier: 0 }]
    }));
  };

  const handleRemoveSupplement = (index) => {
    setFormData(prev => ({
      ...prev,
      supplements: prev.supplements.filter((_, i) => i !== index)
    }));
  };

  const handleUpdateSupplement = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      supplements: prev.supplements.map((sup, i) => {
        if (i !== index) return sup;
        if (field === 'price_modifier') return { ...sup, price_modifier: parseFloat(value) || 0 };
        if (field === 'name_es') return { ...sup, name: { ...sup.name, es: value } };
        return sup;
      })
    }));
  };

  const handleTranslateSupplements = async () => {
    if (formData.supplements.length === 0) return;
    setTranslating(true);
    try {
      const translatedSupplements = await Promise.all(
        formData.supplements.map(async (sup) => {
          if (!sup.name?.es) return sup;
          const res = await fetch(`${API_URL}/api/translate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: sup.name.es, source_lang: 'es', target_langs: ['cat', 'en', 'fr'] })
          });
          const data = await res.json();
          return { ...sup, name: data.translations || sup.name };
        })
      );
      setFormData(prev => ({ ...prev, supplements: translatedSupplements }));
    } catch (error) {
      console.error('Error translating supplements:', error);
    } finally {
      setTranslating(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="product-form-dialog max-w-lg p-0 gap-0" data-testid="product-form-dialog">
          <style>{`
            .product-form-dialog {
              display: flex !important;
              flex-direction: column !important;
              max-height: 90vh !important;
              max-height: 90dvh !important;
            }
            @media (max-width: 639px) {
              .product-form-dialog {
                position: fixed !important;
                top: 0 !important;
                left: 0 !important;
                right: 0 !important;
                bottom: 0 !important;
                transform: none !important;
                max-height: 100dvh !important;
                width: 100% !important;
                max-width: 100% !important;
                border-radius: 0 !important;
              }
            }
            .product-form-scroll {
              -webkit-overflow-scrolling: touch;
              overscroll-behavior: contain;
              min-height: 0;
            }
          `}</style>
          <DialogHeader className="px-4 pt-4 pb-2 sm:px-6 sm:pt-6 flex-shrink-0 border-b">
            <DialogTitle>{editingProduct ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle>
          </DialogHeader>
          
          <div className="product-form-scroll flex-1 overflow-y-auto px-4 sm:px-6 pb-4 space-y-4 pt-3">
            {/* Names with auto-translate */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Nombre</label>
                <Button type="button" variant="outline" size="sm" onClick={handleAutoTranslate} disabled={translating} className="text-xs h-7">
                  {translating ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Languages size={14} className="mr-1" />}
                  Traducir auto
                </Button>
              </div>
              <Input
                value={formData.name.es}
                onChange={(e) => setFormData({ ...formData, name: { ...formData.name, es: e.target.value } })}
                placeholder="Nombre en español *"
              />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Input value={formData.name.cat} onChange={(e) => setFormData({ ...formData, name: { ...formData.name, cat: e.target.value } })} placeholder="Català" className="text-sm" />
                <Input value={formData.name.en} onChange={(e) => setFormData({ ...formData, name: { ...formData.name, en: e.target.value } })} placeholder="English" className="text-sm" />
                <Input value={formData.name.fr} onChange={(e) => setFormData({ ...formData, name: { ...formData.name, fr: e.target.value } })} placeholder="Français" className="text-sm" />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Descripción</label>
              <textarea
                value={formData.description.es}
                onChange={(e) => setFormData({ ...formData, description: { ...formData.description, es: e.target.value } })}
                placeholder="Descripción en español"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm min-h-[80px] resize-none"
              />
            </div>

            {/* Sección (routing) */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 block">Sección de elaboración</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'barra', label: 'Barra', color: 'bg-purple-50 border-purple-300 text-purple-700', icon: '🍸' },
                  { value: 'cocina', label: 'Cocina', color: 'bg-orange-50 border-orange-300 text-orange-700', icon: '🔥' },
                  { value: 'tienda', label: 'Tienda', color: 'bg-teal-50 border-teal-300 text-teal-700', icon: '🛒' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, seccion: opt.value })}
                    className={`p-3 rounded-lg border-2 text-center transition-all ${
                      formData.seccion === opt.value 
                        ? `${opt.color} font-semibold ring-2 ring-offset-1 ring-current` 
                        : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    <span className="text-lg block mb-1">{opt.icon}</span>
                    <span className="text-sm">{opt.label}</span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400">Determina a qué panel se envía el pedido (barra, cocina, o solo tienda)</p>
            </div>

            {/* Price & Category */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Precio (€) *</label>
                <Input
                  type="number" step="0.01" min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Categoría</label>
                <Select
                  value={formData.category_id || '_none'}
                  onValueChange={(v) => setFormData({ ...formData, category_id: v === '_none' ? '' : v })}
                >
                  <SelectTrigger className="w-full text-sm">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name?.es || cat.id}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Image */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 block">Imagen</label>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  {formData.image_url ? (
                    <div className="flex items-center gap-2 p-2 border border-gray-200 rounded-lg bg-gray-50">
                      <img src={formData.image_url} alt="Preview" className="w-12 h-12 object-cover rounded" />
                      <span className="flex-1 text-sm text-gray-600 truncate">{formData.image_url.split('/').pop()}</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setFormData({ ...formData, image_url: '' })} className="h-8 w-8 p-0 text-red-500">
                        <X size={16} />
                      </Button>
                    </div>
                  ) : (
                    <div className="p-4 border-2 border-dashed border-gray-200 rounded-lg text-center">
                      <ImageIcon size={24} className="mx-auto text-gray-300 mb-1" />
                      <p className="text-xs text-gray-400">Sin imagen</p>
                    </div>
                  )}
                </div>
                <Button type="button" variant="outline" onClick={() => setShowImageLibrary(true)} className="flex-shrink-0">
                  <ImageIcon size={16} className="mr-1" /> Biblioteca
                </Button>
              </div>
            </div>

            {/* Supplements */}
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Extras / Suplementos</label>
                <div className="flex gap-2">
                  {formData.supplements.length > 0 && (
                    <Button type="button" variant="outline" size="sm" onClick={handleTranslateSupplements} disabled={translating} className="text-xs h-7">
                      <Languages size={14} className="mr-1" /> Traducir
                    </Button>
                  )}
                  <Button type="button" variant="outline" size="sm" onClick={handleAddSupplement} className="text-xs h-7">
                    <PlusCircle size={14} className="mr-1" /> Añadir
                  </Button>
                </div>
              </div>
              
              {formData.supplements.length === 0 ? (
                <p className="text-xs text-gray-400 italic">No hay extras configurados</p>
              ) : (
                <div className="space-y-2">
                  {formData.supplements.map((sup, index) => (
                    <div key={index} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 p-2 bg-gray-50 rounded-lg">
                      <Input value={sup.name?.es || ''} onChange={(e) => handleUpdateSupplement(index, 'name_es', e.target.value)} placeholder="Nombre del extra" className="flex-1 text-sm h-9" />
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500">+</span>
                        <Input type="number" step="0.1" min="0" value={sup.price_modifier || 0} onChange={(e) => handleUpdateSupplement(index, 'price_modifier', e.target.value)} className="w-20 text-sm h-9" />
                        <span className="text-xs text-gray-500">€</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveSupplement(index)} className="h-9 w-9 p-0 text-red-500 hover:text-red-700 flex-shrink-0">
                          <MinusCircle size={16} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Availability */}
            <div className="flex items-center gap-3 border-t pt-4">
              <input type="checkbox" id="is_active" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} className="w-4 h-4 rounded border-gray-300" />
              <label htmlFor="is_active" className="text-sm text-gray-700">Producto disponible</label>
            </div>

            {/* Badges */}
            <div className="space-y-4 border-t pt-4">
              <h3 className="text-sm font-semibold text-gray-700">Sellos y Badges</h3>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-600">Sello de Origen / Calidad (por idioma)</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'es', flag: 'ES', placeholder: 'DO Empordà' },
                    { key: 'cat', flag: 'CAT', placeholder: 'DO Empordà' },
                    { key: 'en', flag: 'EN', placeholder: 'PDO Empordà' },
                    { key: 'fr', flag: 'FR', placeholder: 'AOP Empordà' },
                  ].map(lang => (
                    <div key={lang.key}>
                      <label className="text-xs text-gray-400">{lang.flag}</label>
                      <Input
                        value={formData.origin_seal?.[lang.key] || ''}
                        onChange={(e) => setFormData({ ...formData, origin_seal: { ...formData.origin_seal, [lang.key]: e.target.value } })}
                        placeholder={lang.placeholder}
                        className="text-sm"
                      />
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-400">Si se rellena, aparecerá como sello destacado en el producto</p>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-600">Badges Secundarios</label>
                <p className="text-xs text-gray-400 mb-2">Recomendación: máximo 2-4 badges por producto</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {BADGE_OPTIONS.map((badge) => (
                    <label 
                      key={badge.key}
                      className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${
                        formData[badge.key] 
                          ? 'bg-green-50 border-green-300 text-green-800' 
                          : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData[badge.key] || false}
                        onChange={(e) => setFormData({ ...formData, [badge.key]: e.target.checked })}
                        className="w-3.5 h-3.5 rounded border-gray-300"
                      />
                      <span className="text-sm">{badge.icon} {badge.label.es}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Emotional Badge Type (Marketing Label) */}
              <div className="space-y-2 mt-4">
                <label className="text-sm font-medium text-gray-600">Etiqueta en Carta</label>
                <p className="text-xs text-gray-400">Se muestra al abrir el producto en la carta digital</p>
                <Select
                  value={formData.badge_type || 'none'}
                  onValueChange={(v) => setFormData({ ...formData, badge_type: v })}
                >
                  <SelectTrigger className="w-full text-sm" data-testid="product-badge-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin etiqueta</SelectItem>
                    <SelectItem value="popular">El mas pedido hoy</SelectItem>
                    <SelectItem value="recommended">Recomendado por la casa</SelectItem>
                    <SelectItem value="chef">Seleccion del chef</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Suggestions / Cross-sell Configuration */}
              <div className="space-y-3 mt-4 p-3 rounded-lg" style={{ backgroundColor: 'rgba(183,140,60,0.06)', border: '1px solid rgba(183,140,60,0.15)' }}>
                <div>
                  <label className="text-sm font-medium text-gray-700">Sugerencias al cliente</label>
                  <p className="text-xs text-gray-400">Productos que aparecen al anadir este al pedido (max 3)</p>
                </div>
                <Select
                  value={formData.suggestions_mode || 'none'}
                  onValueChange={(v) => setFormData({ ...formData, suggestions_mode: v })}
                >
                  <SelectTrigger className="w-full text-sm" data-testid="product-suggestions-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Desactivado</SelectItem>
                    <SelectItem value="manual">Manual (seleccionar productos)</SelectItem>
                    <SelectItem value="auto">Automatico (IA)</SelectItem>
                  </SelectContent>
                </Select>
                
                {formData.suggestions_mode === 'manual' && (
                  <div className="space-y-2">
                    {(formData.recommended_products || []).map((prodId, idx) => {
                      const sugProd = allProducts.find(p => p.id === prodId);
                      return (
                        <div key={idx} className="flex items-center gap-2 p-2 rounded-md bg-white border border-gray-100">
                          <span className="flex-1 text-sm truncate">{sugProd?.name?.es || 'Producto no encontrado'}</span>
                          <span className="text-xs font-medium text-amber-600">{sugProd?.price?.toFixed(2)}EUR</span>
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              recommended_products: prev.recommended_products.filter((_, i) => i !== idx)
                            }))}
                            className="text-red-400 hover:text-red-600 p-1"
                          >
                            <MinusCircle size={16} />
                          </button>
                        </div>
                      );
                    })}
                    {(formData.recommended_products || []).length < 3 && (
                      <Select
                        value=""
                        onValueChange={(v) => {
                          if (v) {
                            setFormData(prev => ({
                              ...prev,
                              recommended_products: [...(prev.recommended_products || []), v]
                            }));
                          }
                        }}
                      >
                        <SelectTrigger className="w-full border-dashed text-sm text-gray-500" data-testid="add-suggestion-select">
                          <SelectValue placeholder="+ Anadir sugerencia..." />
                        </SelectTrigger>
                        <SelectContent>
                          {allProducts
                            .filter(p => p.id !== editingProduct?.id && !(formData.recommended_products || []).includes(p.id) && p.is_active !== false)
                            .map(p => (
                              <SelectItem key={p.id} value={p.id}>{p.name?.es} — {p.price?.toFixed(2)}€</SelectItem>
                            ))
                          }
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
                
                {formData.suggestions_mode === 'auto' && (
                  <p className="text-xs text-amber-600 italic">La IA sugerira productos complementarios automaticamente basandose en la categoria y precio.</p>
                )}
              </div>

              {/* ========== PRODUCT TYPE: SIMPLE vs EXPERIENCE ========== */}
              <div className="space-y-3 mt-4 p-3 rounded-lg" style={{ backgroundColor: 'rgba(218,165,32,0.08)', border: '1px solid rgba(218,165,32,0.2)' }}>
                <div>
                  <label className="text-sm font-medium text-gray-700">Tipo de producto</label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'simple', label: 'Simple', desc: 'Producto individual' },
                    { value: 'experience', label: 'Experiencia / Pack', desc: 'Pack con varios items' },
                  ].map(opt => (
                    <button key={opt.value} type="button"
                      onClick={() => setFormData({ ...formData, product_type: opt.value })}
                      className={`p-3 rounded-lg border-2 text-center transition-all text-sm ${
                        formData.product_type === opt.value
                          ? 'bg-amber-50 border-amber-400 text-amber-800 font-semibold'
                          : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                      }`}
                      data-testid={`product-type-${opt.value}`}
                    >
                      {opt.label}
                      <p className="text-xs font-normal mt-0.5 opacity-70">{opt.desc}</p>
                    </button>
                  ))}
                </div>

                {formData.product_type === 'experience' && (
                  <div className="space-y-3 pt-2">
                    {/* Target people */}
                    <div>
                      <label className="text-xs text-gray-500">Para cuantas personas</label>
                      <div className="flex gap-2 mt-1">
                        {['1', '2-3', '4+'].map(v => (
                          <button key={v} type="button"
                            onClick={() => setFormData({ ...formData, experience_target_people: v })}
                            className={`px-3 py-1.5 rounded-lg text-sm border ${
                              formData.experience_target_people === v
                                ? 'bg-amber-100 border-amber-400 font-medium' : 'border-gray-200'
                            }`}
                          >{v} pers.</button>
                        ))}
                      </div>
                    </div>

                    {/* Experience items */}
                    <div>
                      <label className="text-xs text-gray-500">Contenido del pack</label>
                      {(formData.experience_items || []).map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 mt-1">
                          <input
                            value={item?.es || ''}
                            onChange={(e) => {
                              const items = [...(formData.experience_items || [])];
                              items[idx] = { ...items[idx], es: e.target.value };
                              setFormData({ ...formData, experience_items: items });
                            }}
                            className="flex-1 px-2 py-1.5 border rounded-lg text-sm"
                            placeholder="Ej: Cafe o matcha (a elegir)"
                          />
                          <button type="button" onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              experience_items: prev.experience_items.filter((_, i) => i !== idx)
                            }));
                          }} className="text-red-400 hover:text-red-600 p-1">
                            <MinusCircle size={14} />
                          </button>
                        </div>
                      ))}
                      <button type="button" onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          experience_items: [...(prev.experience_items || []), { es: '', en: '', cat: '', fr: '' }]
                        }));
                      }} className="text-xs text-amber-600 mt-2 hover:underline">+ Anadir item al pack</button>
                    </div>

                    {/* Experience copy */}
                    <div>
                      <label className="text-xs text-gray-500">Copy para la carta (ES)</label>
                      <textarea
                        value={formData.experience_copy?.es || ''}
                        onChange={(e) => setFormData({ ...formData, experience_copy: { ...formData.experience_copy, es: e.target.value } })}
                        className="w-full px-2 py-1.5 border rounded-lg text-sm min-h-[60px] resize-none mt-1"
                        placeholder="Ej: Empieza el dia con calma. Cafe especialidad, zumo natural..."
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* ========== SHOP INTEGRATION ========== */}
              <div className="space-y-2 mt-4 p-3 rounded-lg bg-teal-50/50 border border-teal-200/50">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Disponible en tienda</label>
                    <p className="text-xs text-gray-400">Muestra "Disponible en tienda" en la ficha</p>
                  </div>
                  <Switch
                    checked={formData.available_in_shop || false}
                    onCheckedChange={(v) => setFormData({ ...formData, available_in_shop: v })}
                    data-testid="product-shop-switch"
                  />
                </div>
                {formData.available_in_shop && (
                  <input
                    value={formData.shop_message?.es || ''}
                    onChange={(e) => setFormData({ ...formData, shop_message: { ...formData.shop_message, es: e.target.value } })}
                    className="w-full px-2 py-1.5 border rounded-lg text-sm"
                    placeholder="Llevatelo a casa (opcional — se usa texto por defecto)"
                  />
                )}
              </div>

              {/* ========== UPSELL AL AÑADIR ========== */}
              <div className="space-y-2 mt-4 p-3 rounded-lg bg-amber-50/50 border border-amber-200/50">
                <label className="text-sm font-medium text-gray-700">Upsell al añadir</label>
                <p className="text-xs text-gray-400">Al añadir este producto, sugerir otro (ej: Copa → Botella, plato → experiencia)</p>
                <Select
                  value={formData.upsell_product_id || '_none'}
                  onValueChange={(v) => setFormData({ ...formData, upsell_product_id: v === '_none' ? '' : v })}
                >
                  <SelectTrigger className="w-full text-sm" data-testid="upsell-product-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Desactivado</SelectItem>
                    {allProducts
                      .filter(p => p.id !== editingProduct?.id && p.is_active !== false)
                      .map(p => <SelectItem key={p.id} value={p.id}>{p.name?.es} — {p.price?.toFixed(2)}€</SelectItem>)
                    }
                  </SelectContent>
                </Select>
                {formData.upsell_product_id && (
                  <div className="space-y-2">
                    <input value={formData.upsell_title?.es || ''}
                      onChange={(e) => setFormData({ ...formData, upsell_title: { ...formData.upsell_title, es: e.target.value } })}
                      className="w-full px-2 py-1.5 border rounded-lg text-sm" placeholder="Titulo popup (ej: Sube de nivel)" />
                    <input value={formData.upsell_description?.es || ''}
                      onChange={(e) => setFormData({ ...formData, upsell_description: { ...formData.upsell_description, es: e.target.value } })}
                      className="w-full px-2 py-1.5 border rounded-lg text-sm" placeholder="Descripcion (ej: La botella es perfecta para compartir)" />
                  </div>
                )}
              </div>

              {/* ========== DOWNSELL (SI RECHAZA) ========== */}
              {formData.upsell_product_id && (
                <div className="space-y-2 mt-4 p-3 rounded-lg bg-green-50/50 border border-green-200/50">
                  <label className="text-sm font-medium text-gray-700">Downsell (si rechaza el upsell)</label>
                  <p className="text-xs text-gray-400">Si dicen que no al upsell, ofrecer una alternativa más asequible</p>
                  <Select
                    value={formData.downsell_product_id || '_none'}
                    onValueChange={(v) => setFormData({ ...formData, downsell_product_id: v === '_none' ? '' : v })}
                  >
                    <SelectTrigger className="w-full text-sm" data-testid="downsell-product-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">Desactivado</SelectItem>
                      {allProducts
                        .filter(p => p.id !== editingProduct?.id && p.id !== formData.upsell_product_id && p.is_active !== false)
                        .map(p => <SelectItem key={p.id} value={p.id}>{p.name?.es} — {p.price?.toFixed(2)}€</SelectItem>)
                      }
                    </SelectContent>
                  </Select>
                  {formData.downsell_product_id && (
                    <div className="space-y-2">
                      <input value={formData.downsell_title?.es || ''}
                        onChange={(e) => setFormData({ ...formData, downsell_title: { ...formData.downsell_title, es: e.target.value } })}
                        className="w-full px-2 py-1.5 border rounded-lg text-sm" placeholder="Titulo (ej: Te puede interesar...)" />
                      <input value={formData.downsell_description?.es || ''}
                        onChange={(e) => setFormData({ ...formData, downsell_description: { ...formData.downsell_description, es: e.target.value } })}
                        className="w-full px-2 py-1.5 border rounded-lg text-sm" placeholder="Descripcion (ej: Perfecto para acompañar)" />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2 px-4 py-3 sm:px-6 sm:py-4 border-t flex-shrink-0">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              <X size={16} className="mr-1" /> Cancelar
            </Button>
            <Button onClick={onSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
              {saving ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Save size={16} className="mr-1" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImageLibrary
        open={showImageLibrary}
        onClose={() => setShowImageLibrary(false)}
        onSelect={(imageUrl) => setFormData({ ...formData, image_url: imageUrl })}
        currentImage={formData.image_url}
      />
    </>
  );
};

export default ProductFormDialog;
